/**
 * Client-side RBAC permission gating.
 * Mirrors backend `has_perm` / `is_admin_user` / ownership logic from
 * `/app/backend/deps.py` so buttons hide before a 403 is ever returned.
 *
 * Permissions on the user object are either the wildcard "*",
 * a bare module name like "customers" (grants ALL actions on that module),
 * or "module.action" like "customers.edit".
 */
import { useMemo } from "react";
import { useAuth } from "@/context/AuthContext";

/** True if the caller is a Platform Admin (email in PLATFORM_ADMIN_EMAILS
 *  on the backend). Set on the user object by `public_user`. */
export function isPlatformAdmin(user) {
  return !!(user && user.is_platform_admin);
}

/** True if role always sees & manages every record in the tenant. */
export function isAdminUser(user) {
  if (!user) return false;
  return user.role === "organization_owner" || user.role === "admin";
}

/** Matches backend `has_perm`. Platform admins and Super Admins
 *  (organization_owner / admin) bypass all module checks — new modules
 *  added in the future are automatically accessible to them. */
export function hasPerm(user, perm) {
  if (!user) return false;
  if (isPlatformAdmin(user)) return true;
  if (isAdminUser(user)) return true;
  const perms = user.permissions || [];
  if (perms.includes("*")) return true;
  if (perms.includes(perm)) return true;
  if (perm.includes(".")) {
    const mod = perm.split(".", 1)[0];
    if (perms.includes(mod)) return true;
  }
  return false;
}

/** True if the user can act on a specific owned record.
 * Backend visibility rule: admins see all; sales_manager sees own team;
 * sales_executive & custom see only their own owner_id.
 * If the record was already returned by the API list, the visibility
 * filter has already passed — this is a defensive extra check when the
 * caller needs to distinguish "in your scope" vs "you own it directly". */
export function isOwnRecord(user, record) {
  if (!user || !record) return false;
  if (isAdminUser(user)) return true;
  // Sales manager can act on team records too (backend enforces the team scope).
  if (user.role === "sales_manager") return true;
  return record.owner_id === user.id || record.created_by === user.id;
}

/** Hook returning helpers bound to the current user. */
export function usePermissions() {
  const { user } = useAuth();
  return useMemo(() => ({
    user,
    isAdmin: isAdminUser(user),
    isPlatformAdmin: isPlatformAdmin(user),
    can: (perm) => hasPerm(user, perm),
    canAny: (perms) => perms.some((p) => hasPerm(user, p)),
    canEdit: (perm, record) => hasPerm(user, perm) && isOwnRecord(user, record),
    canDelete: (perm, record) => hasPerm(user, perm) && isOwnRecord(user, record),
  }), [user]);
}
